import React, { useState, useRef, useEffect } from 'react';
import { Link2, X, Loader2 } from 'lucide-react';
import { apiRequest } from '../../config/api';

/**
 * LinkField - URL input with optional internal page linking
 *
 * Supports two value formats:
 *   - External link: plain string (e.g. "https://example.com")
 *   - Internal page link: { type: "internal", pageId: "uuid", pageTitle: "Page Name" }
 *
 * When the page belongs to a site (page.site_id exists), a link icon button appears
 * next to the input that opens a dropdown of pages in the site for internal linking.
 * When there is no site context, it renders as a plain text input identical to TextField.
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {string} [props.field.placeholder] - Placeholder text
 * @param {string} [props.field.default] - Default value
 * @param {string} [props.field.description] - Optional description text
 * @param {string|Object} props.value - Current value (string or internal link object)
 * @param {Function} props.onChange - Callback when value changes
 * @param {Object} [props.page] - Page context with id and site_id
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.compact] - Use compact inline layout
 */
export default function LinkField({ field, value, onChange, page, disabled = false, error, compact = false }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const isInternalLink = value && typeof value === 'object' && value.type === 'internal';
  const hasSite = page && page.site_id;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Fetch pages when dropdown opens
  useEffect(() => {
    if (!dropdownOpen || !hasSite) return;

    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    apiRequest(`/sites/${page.site_id}/pages`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch pages');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        // Handle both array responses and { pages: [...] } wrapper
        const pageList = Array.isArray(data) ? data : (data.pages || []);
        // Exclude the current page from the list
        const filtered = pageList.filter((p) => p.id !== page.id);
        setPages(filtered);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(err.message || 'Failed to load pages');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [dropdownOpen, hasSite, page?.site_id, page?.id]);

  const handleTextChange = (e) => {
    onChange(e.target.value);
  };

  const handleSelectPage = (selectedPage) => {
    onChange({
      type: 'internal',
      pageId: selectedPage.id,
      pageTitle: selectedPage.title,
    });
    setDropdownOpen(false);
  };

  const handleClearInternal = () => {
    onChange('');
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  // Build the input element
  const inputElement = (
    <div className="relative">
      <div className="flex items-center gap-1">
        {isInternalLink ? (
          // Internal link display: read-only input with page badge and clear button
          <div className={`flex items-center w-full px-2 py-1 text-xs border rounded bg-gray-50 ${
            error
              ? 'border-red-500'
              : 'border-gray-200'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 mr-1.5 flex-shrink-0">
              Page
            </span>
            <span className="truncate flex-1 text-gray-800">{value.pageTitle}</span>
            {!disabled && (
              <button
                type="button"
                onClick={handleClearInternal}
                className="ml-1 p-0.5 text-gray-400 hover:text-gray-600 flex-shrink-0 rounded hover:bg-gray-200"
                title="Clear internal link"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ) : (
          // External URL text input
          <input
            type="text"
            value={value ?? ''}
            onChange={handleTextChange}
            disabled={disabled}
            placeholder={field.placeholder || field.default || 'https://...'}
            className={`w-full px-2 py-1 text-xs border rounded bg-gray-50 ${
              error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
        )}

        {/* Link icon button -- only shown when the page belongs to a site */}
        {hasSite && !disabled && (
          <button
            ref={buttonRef}
            type="button"
            onClick={toggleDropdown}
            className={`flex-shrink-0 p-1 rounded border text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors ${
              dropdownOpen ? 'border-blue-400 text-blue-600 bg-blue-50' : 'border-gray-200 bg-gray-50'
            }`}
            title="Link to a page in this site"
          >
            <Link2 size={14} />
          </button>
        )}
      </div>

      {/* Internal pages dropdown */}
      {dropdownOpen && hasSite && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          {loading && (
            <div className="flex items-center justify-center gap-1.5 py-3 text-xs text-gray-500">
              <Loader2 size={14} className="animate-spin" />
              <span>Loading pages...</span>
            </div>
          )}

          {fetchError && (
            <div className="px-3 py-2 text-xs text-red-500">
              {fetchError}
            </div>
          )}

          {!loading && !fetchError && pages.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">
              No other pages in this site
            </div>
          )}

          {!loading && !fetchError && pages.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleSelectPage(p)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 hover:text-blue-700 transition-colors truncate"
            >
              {p.title || 'Untitled'}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Compact mode: inline label + control
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex-1 min-w-0">
          {inputElement}
        </div>
      </div>
    );
  }

  // Standard mode: stacked layout
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {field.label}
      </label>
      {inputElement}
      {field.description && (
        <p className="mt-1 text-xs text-gray-500">{field.description}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
